<template>
  <div v-if="open.length" class="weekdays-container">
    <ul class="weekdays">
      <li
        v-for="day in mapped"
        :key="day.id"
        :title="day.label"
        :class="{ 'is-disabled': day.disabled }"
      >
        <span>{{ day.short }}</span>
      </li>
    </ul>
  </div>
</template>

<script lang="ts" setup>
import { notNullish } from '@vueuse/core'

const props = withDefaults(
  defineProps<{
    open?: string[]
    title?: string
  }>(),
  {
    open: () => [],
    title: '',
  },
)

const language = useCurrentLanguage()

const { data } = await useCachedAsyncData(
  'weekdays-' + language.value,
  () =>
    useGraphqlQuery('weekdays', null, {
      graphqlCaching: {
        client: true,
      },
    }),
  {
    transform: (data) => {
      const items = (data.data.entityQuery.items || [])
        .map((v) => {
          if (v && 'tid' in v && v.tid && v.name) {
            return {
              id: v.tid,
              name: v.name,
            }
          }

          return null
        })
        .filter(notNullish)

      return { items, cacheability: data.extensions?.cacheability }
    },
    deep: false,
    serverMaxAge: 60 * 60 * 24,
    clientMaxAge: 'never',
    serverCacheTags: (data) => data.extensions?.cacheability?.tagsNuxt || [],
    watch: [language],
  },
)

useGraphqlCacheability(data.value)

const mapped = computed(() => {
  return data.value?.items.map((v) => {
    return {
      short: v.name?.substr(0, 2),
      id: v.id,
      label: v.name,
      disabled: !props.open.includes(v.id),
    }
  })
})
</script>

<style lang="postcss">
.weekdays {
  @apply inline-flex;
  @apply belowTablet:w-full belowTablet:justify-between;
  @screen belowTablet {
    max-width: 360px;
  }
  li {
    @apply flex items-center justify-center rounded-full bg-scheme-invert text-[.8rem] font-bold uppercase text-scheme-main;
    @apply tablet:text-size-7;
    @apply widescreen:text-size-6;
    width: 2.5em;
    height: 2.5em;
    &.is-disabled {
      @apply bg-border;
    }
    @screen tablet {
      &:not(:last-child) {
        margin-right: 0.75em;
      }
    }
    span {
      display: inline-block;
      margin-top: 0.1em;
    }
  }
}
.weekdays-container {
  &:not(:last-child) {
    @apply mb-size-5;
  }
  @apply desktop:flex desktop:items-center;
  .title {
    @apply desktop:m-0;
    @screen desktop {
      flex: 0 0 50%;
      margin-right: auto;
    }
  }
}
</style>
